import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Hammer, Globe, LogOut, LayoutDashboard, Menu, MessageSquare, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from "react";
import api from "@/api";

const langs = { en: "EN", fr: "FR", ar: "AR", de: "DE" };

export const Header = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const [unread, setUnread] = useState(0); // Unterhaltungen, auf die noch geantwortet werden muss
  const loggedIn0 = !!(user && user !== false);
  useEffect(() => {
    if (!loggedIn0) { setUnread(0); return undefined; }
    const load = () => api.get("/threads/unread-count").then((r) => setUnread(r.data.open || 0)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    window.addEventListener("mm:unread", load);
    return () => { clearInterval(id); window.removeEventListener("mm:unread", load); };
  }, [loggedIn0, loc.pathname]);

  const loggedIn = user && user !== false;
  const role = loggedIn ? user.role : null;
  const isCustomer = role === "customer";
  const isCraftsman = role === "craftsman";
  const postTo = isCustomer ? "/post-job" : "/register?role=customer";
  const jobsTo = isCraftsman ? "/jobs" : "/register?role=craftsman";
  const lk = "text-base font-semibold text-[#5A4B45] hover:text-[#C65338] transition-colors whitespace-nowrap";

  const ctas = (
    <>
      {!isCustomer && (
        <Link to={role && !isCraftsman ? "/jobs" : jobsTo} data-testid="cta-find-jobs" onClick={() => setOpen(false)}
          className="inline-flex items-center justify-center rounded-full border-2 border-[#5C6646] text-[#5C6646] hover:bg-[#5C6646] hover:text-white text-sm font-bold px-4 py-1.5 transition-colors whitespace-nowrap">{t("nav.findJobs")}</Link>
      )}
      {!isCraftsman && (
        <Link to={role && !isCustomer ? "/post-job" : postTo} data-testid="cta-post-job" onClick={() => setOpen(false)}
          className="inline-flex items-center justify-center rounded-full border-2 border-[#C65338] text-[#C65338] hover:bg-[#C65338] hover:text-white text-sm font-bold px-4 py-1.5 transition-colors whitespace-nowrap">{t("nav.postFree")}</Link>
      )}
    </>
  );

  const links = (
    <>
      {isCustomer && <Link to="/dashboard" data-testid="nav-myjobs" className={lk}>{t("nav.myJobs")}</Link>}
      {isCraftsman && <Link to="/applications" data-testid="nav-myapps" className={lk}>{t("nav.myApps")}</Link>}
      <Link to="/map" data-testid="nav-map" className="text-base font-semibold text-[#5A4B45] hover:text-[#C65338] transition-colors">{t("nav.map")}</Link>
      <a href="/#pricing" data-testid="nav-pricing" className="text-base font-semibold text-[#5A4B45] hover:text-[#C65338] transition-colors">{t("nav.pricing")}</a>
    </>
  );

  const topbar = !loggedIn && (
    <div data-testid="topbar" className="bg-[#EFE6DC] text-[#2D231F] text-sm text-center px-4 py-2.5">
      {t("topbar.text")}{" "}
      <Link to="/register?role=craftsman" data-testid="topbar-register" className="font-bold underline underline-offset-2 hover:text-[#C65338]">{t("topbar.link")}</Link>
    </div>
  );

  return (
    <>
    {topbar}
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF5F0]/80 border-b border-[#EBDDD3]/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-44 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo" className="flex items-center shrink-0 transition-transform hover:scale-105">
          <img src="/mymoalem-header-logo.png" alt="MyMoalem" className="h-40 w-auto" />
        </Link>

        <div data-testid="header-ctas" className="hidden md:flex items-center gap-3">{ctas}</div>

        <div className="flex items-center gap-2">
          <nav className="hidden md:flex items-center gap-5 lg:gap-6 mr-2">{links}</nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="lang-switcher" className="rounded-full gap-1.5 text-base text-[#5A4B45]">
                <Globe className="w-4 h-4" /> {langs[lang]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.keys(langs).map((l) => (
                <DropdownMenuItem key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}>{langs[l]}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && user !== false ? (
            <div className="hidden md:flex items-center gap-2">
              {user.role === "admin" && (
                <Button variant="ghost" size="sm" data-testid="nav-admin" onClick={() => nav("/admin")} className="rounded-full gap-1.5 text-base">
                  <Shield className="w-4 h-4" /> {t("nav.admin")}
                </Button>
              )}
              <Button variant="ghost" size="icon" data-testid="nav-messages" onClick={() => nav("/messages")} className={`rounded-full gap-1 ${unread > 0 ? "w-auto px-3 bg-[#E69625] hover:bg-[#cf8520] text-white" : ""}`}>
                <MessageSquare className="w-4 h-4" />
                {unread > 0 && <span data-testid="unread-badge" className="text-sm font-bold leading-none">{unread > 99 ? "99+" : unread}</span>}
              </Button>
              <Button variant="ghost" size="sm" data-testid="nav-dashboard" onClick={() => nav(isCustomer ? "/profile" : "/dashboard")} className="rounded-full gap-1.5 text-base">
                <LayoutDashboard className="w-4 h-4" /> {isCraftsman || isCustomer ? t("nav.myProfile") : t("nav.dashboard")}
              </Button>
              <Button variant="ghost" size="icon" data-testid="nav-logout" onClick={async () => { await logout(); nav("/"); }} className="rounded-full">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" size="sm" data-testid="nav-login" onClick={() => nav("/login")} className="rounded-full text-base">{t("nav.login")}</Button>
            </div>
          )}

          <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu" onClick={() => setOpen(!open)}>
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EBDDD3] bg-[#FAF5F0] px-5 py-4 flex flex-col gap-3">
          <div className="flex flex-col gap-2">{ctas}</div>
          {links}
          {user && user !== false ? (
            <>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#5A4B45]">{t("nav.admin")}</Link>}
              <Link to="/messages" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#5A4B45]">{t("nav.messages")}{unread > 0 ? ` (${unread})` : ""}</Link>
              <Link to={isCustomer ? "/profile" : "/dashboard"} onClick={() => setOpen(false)} className="text-sm font-semibold text-[#5A4B45]">{isCraftsman || isCustomer ? t("nav.myProfile") : t("nav.dashboard")}</Link>
              <button onClick={async () => { await logout(); nav("/"); setOpen(false); }} className="text-sm font-semibold text-left text-[#5A4B45]">{t("nav.logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#5A4B45]">{t("nav.login")}</Link>
            </>
          )}
        </div>
      )}
    </header>
    </>
  );
};